import useStyles from 'isomorphic-style-loader/useStyles';
import { Button, Checkbox, Icon, Popup } from 'semantic-ui-react';
import { useLocalStorage } from 'usehooks-ts';

import { useIsDarkMode } from './dark-mode-provider';
import s from './dark-mode-toggle.scss';

/**
 * A toggle switch to enable or disable dark mode. This is bound to a value in
 * local storage to persist the user preference on subsequent visits. Initially
 * that will be empty, so the toggle value reflects the system preference.
 *
 * This component does not directly apply the dark mode styles - That is the
 * responsibility of the `DarkReaderProvider`, which listens to the same local
 * storage key.
 *
 * @see DarkModeProvider
 */
export const DarkModeToggle = () => {
  useStyles(s);

  // Read the current state from context.
  // This is always `false` on the server. Pending updates are gated until after
  // client hydration to avoid a mismatch errors in the shell.
  const isDarkMode = useIsDarkMode();

  // Use a local storage setter to persist the user's preference.
  // We don't need the getter as it's handled via context.
  const [, setIsDarkMode] = useLocalStorage('darkmode', false);

  /**
   * When the user toggles the state, persist it to local storage so it can be
   * restored as a preference on subsequent visits. This triggers a side effect
   * in the dark mode provider and overrides the system preference.
   */
  const toggleDarkMode = (value?: boolean) => {
    setIsDarkMode(value ?? !isDarkMode);
  };

  return (
    <div className={s.root}>
      <span>
        <Popup
          content="Here comes the sun, and I say, it's alright..."
          position="bottom center"
          mouseEnterDelay={1000}
          trigger={
            <Button icon color="black" onClick={() => toggleDarkMode(false)}>
              <Icon name="sun" color="yellow" className={s.sun} />
            </Button>
          }
        />

        <Checkbox
          slider
          className={isDarkMode ? s.toggleDark : s.toggleLight}
          checked={isDarkMode}
          onChange={() => toggleDarkMode()}
        />

        <Popup
          content="Hello darkness my old friend..."
          position="bottom center"
          mouseEnterDelay={1000}
          trigger={
            <Button icon color="black" onClick={() => toggleDarkMode(true)}>
              <Icon name="moon" color="yellow" />
            </Button>
          }
        />
      </span>
    </div>
  );
};

export default DarkModeToggle;
